/**
 * Interaction / visibility keep-alive for JWT sessions.
 *
 * Slides the access token via GET /check_session?slide=1 while the user is active
 * and the token is approaching expiry.
 */

import {
	getKeepAliveThresholdSeconds,
	getTokenRemainingTime,
	Util,
} from './Util';
import { getSessionAccessToken, readStoredSession } from './sessionStore';

export type KeepAliveRefreshFn = () => void | Promise<void>;

export type KeepAliveOptions = {
	getSession: () => any | null;
	refresh: KeepAliveRefreshFn;
	debounceMs?: number;
	minIntervalMs?: number;
};

const DEFAULT_DEBOUNCE_MS = 250;
/** Min gap between successful slides (not between attempts when still needed). */
const DEFAULT_MIN_INTERVAL_MS = 3_000;
/** How often to proactively check while the tab is visible. */
const POLL_MS = 10_000;

const INTERACTIVE_SELECTOR = [
	'button',
	'a',
	'input',
	'textarea',
	'select',
	'label',
	'summary',
	'option',
	'[role="button"]',
	'[role="link"]',
	'[role="menuitem"]',
	'[role="tab"]',
	'[role="checkbox"]',
	'[role="radio"]',
	'[role="switch"]',
	'[role="option"]',
	'[role="combobox"]',
	'[role="listbox"]',
	'[role="textbox"]',
	'[role="gridcell"]',
	'[role="row"]',
	'[contenteditable="true"]',
	'[contenteditable=""]',
	'.MuiButtonBase-root',
	'.MuiIconButton-root',
	'.MuiMenuItem-root',
	'.MuiListItemButton-root',
	'.MuiDataGrid-cell',
	'.MuiDataGrid-row',
	'.ag-row',
	'.ag-cell',
].join(',');

function isInteractiveTarget(target: EventTarget | null): boolean {
	if (!(target instanceof Element)) {
		const active = document.activeElement;
		if (active instanceof Element) {
			return Boolean(active.closest(INTERACTIVE_SELECTOR));
		}
		return false;
	}
	return Boolean(target.closest(INTERACTIVE_SELECTOR));
}

/** Configured session lifetime (seconds), never “seconds remaining”. */
export function getSessionLifetimeSeconds(session: any | null): number {
	const stored = session || readStoredSession();
	const candidates = [
		stored?.sessionLifetime,
		stored?.expiry, // buildToken / check_session: configured lifetime
	];
	for (const c of candidates) {
		const n = Number(c);
		// Ignore tiny values that look like a bug; floor is backend min 60.
		if (Number.isFinite(n) && n >= 30) return n;
	}
	return 300;
}

/**
 * True when the JWT still has life left and is inside the slide window.
 * Uses skew=0 so we can still slide in the final seconds before hard expiry.
 */
export function sessionNeedsKeepAlive(session: any | null): boolean {
	const token =
		getSessionAccessToken(session) || getSessionAccessToken(readStoredSession());
	if (!token) return false;

	// Allow slide until the token is actually expired (1s grace only).
	if (!Util.isAccessTokenUsable(token, 1_000)) return false;

	let remaining: number;
	try {
		remaining = getTokenRemainingTime(token);
	} catch {
		return false;
	}
	if (remaining <= 0) return false;

	const lifetime = getSessionLifetimeSeconds(session);
	const threshold = getKeepAliveThresholdSeconds(lifetime);
	// Slide when remaining is under the threshold (inclusive of edge).
	return remaining <= threshold;
}

/**
 * Install keep-alive listeners. Returns a dispose function.
 */
export function installSessionKeepAlive(options: KeepAliveOptions): () => void {
	const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
	const minIntervalMs = options.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS;

	let debounceTimer: ReturnType<typeof setTimeout> | null = null;
	let lastRefreshAt = 0;
	let inFlight = false;
	let disposed = false;
	/** Last time we saw any user activity (used for “active user” slides). */
	let lastActivityAt = Date.now();

	const markActivity = () => {
		lastActivityAt = Date.now();
	};

	const runRefresh = async (force = false) => {
		if (disposed || inFlight) return;

		const session = options.getSession() || readStoredSession();
		if (!sessionNeedsKeepAlive(session) && !force) return;

		// Only slide if the user has been active recently OR this is a forced tick
		// that already passed sessionNeedsKeepAlive (token near expiry + poll).
		const idleMs = Date.now() - lastActivityAt;
		const recentlyActive = idleMs < 90_000; // 90s of “still here”
		if (!force && !recentlyActive && !sessionNeedsKeepAlive(session)) return;

		// If near expiry, slide even if last activity was a bit older, as long as
		// tab is visible — user may be reading. Interaction/poll still gate this.
		if (!sessionNeedsKeepAlive(session)) return;

		const now = Date.now();
		if (now - lastRefreshAt < minIntervalMs) return;

		inFlight = true;
		try {
			await options.refresh();
			lastRefreshAt = Date.now();
		} catch (err) {
			console.warn('Session keep-alive refresh failed', err);
		} finally {
			inFlight = false;
		}
	};

	const scheduleRefresh = () => {
		if (disposed) return;
		if (debounceTimer) clearTimeout(debounceTimer);
		debounceTimer = setTimeout(() => {
			debounceTimer = null;
			void runRefresh();
		}, debounceMs);
	};

	const onUserActivity = (e: Event) => {
		markActivity();
		// Near expiry: any activity slides. Otherwise only interactive targets
		// (avoids mousemove storms early in the session).
		const session = options.getSession() || readStoredSession();
		const nearExpiry = sessionNeedsKeepAlive(session);
		if (nearExpiry) {
			scheduleRefresh();
			return;
		}
		if (e.type === 'keydown' || e.type === 'scroll') {
			// Still track activity; no slide yet.
			return;
		}
		if (isInteractiveTarget(e.target)) {
			// Not near expiry — no need to hit the server.
			return;
		}
	};

	const onKeyDown = (e: KeyboardEvent) => {
		markActivity();
		const session = options.getSession() || readStoredSession();
		if (sessionNeedsKeepAlive(session)) {
			scheduleRefresh();
			return;
		}
		// Early in session: only mark activity.
		void e;
	};

	let scrollTimer: ReturnType<typeof setTimeout> | null = null;
	const onScroll = () => {
		markActivity();
		if (scrollTimer) return;
		scrollTimer = setTimeout(() => {
			scrollTimer = null;
			const session = options.getSession() || readStoredSession();
			if (sessionNeedsKeepAlive(session)) scheduleRefresh();
		}, 800);
	};

	const onFocusOrVisible = () => {
		if (document.visibilityState === 'hidden') return;
		markActivity();
		const session = options.getSession() || readStoredSession();
		if (sessionNeedsKeepAlive(session)) void runRefresh();
	};

	const onVisibility = () => {
		if (document.visibilityState === 'visible') onFocusOrVisible();
	};

	// Capture phase so we see activity even if handlers stop propagation.
	window.addEventListener('click', onUserActivity, true);
	window.addEventListener('pointerdown', onUserActivity, true);
	window.addEventListener('touchstart', onUserActivity, {
		capture: true,
		passive: true,
	});
	window.addEventListener('keydown', onKeyDown, true);
	window.addEventListener('scroll', onScroll, { capture: true, passive: true });
	window.addEventListener('focus', onFocusOrVisible);
	document.addEventListener('visibilitychange', onVisibility);

	// Proactive poll: critical for short TTLs (e.g. 60s) where 30s was too slow.
	const interval = window.setInterval(() => {
		if (document.visibilityState !== 'visible') return;
		const session = options.getSession() || readStoredSession();
		if (!sessionNeedsKeepAlive(session)) return;
		// Poll counts as “still on page” activity for short windows.
		markActivity();
		void runRefresh();
	}, POLL_MS);

	// Immediate check on install (e.g. refresh with an already-near-expiry token).
	void runRefresh();

	return () => {
		disposed = true;
		if (debounceTimer) clearTimeout(debounceTimer);
		if (scrollTimer) clearTimeout(scrollTimer);
		window.clearInterval(interval);
		window.removeEventListener('click', onUserActivity, true);
		window.removeEventListener('pointerdown', onUserActivity, true);
		window.removeEventListener('touchstart', onUserActivity, true);
		window.removeEventListener('keydown', onKeyDown, true);
		window.removeEventListener('scroll', onScroll, true);
		window.removeEventListener('focus', onFocusOrVisible);
		document.removeEventListener('visibilitychange', onVisibility);
	};
}

export function describeKeepAliveWindow(session: any | null): {
	remaining: number;
	threshold: number;
	lifetime: number;
	needsRefresh: boolean;
} {
	const token = getSessionAccessToken(session);
	const lifetime = getSessionLifetimeSeconds(session);
	const threshold = getKeepAliveThresholdSeconds(lifetime);
	if (!token) {
		return { remaining: 0, threshold, lifetime, needsRefresh: false };
	}
	let remaining = 0;
	try {
		remaining = getTokenRemainingTime(token);
	} catch {
		remaining = 0;
	}
	return {
		remaining,
		threshold,
		lifetime,
		needsRefresh: remaining > 0 && remaining <= threshold,
	};
}
